/**
 * Wire contract for `POST /api/v1/payments/stripe/past-due-offer`, the self-serve half of the
 * failed-renewal dunning emails. A customer whose renewal did not go through can keep their plan
 * at 30% off (a credit note takes the open invoice down to that price, never below it, and the
 * coupon carries forward) or, on Dashboard, switch to a longer term at 40% off through Checkout.
 * Same coupons and percentages as the cancellation flow, but none of its bookkeeping: the customer
 * is not cancelling.
 *
 * Both open on day 7 after the charge failed, as the dunning emails make them; before that the
 * route answers 409 (`utils/shared/payments/pastDueOfferWindow`).
 */

export const PAST_DUE_OFFER_ACTIONS = {
  Describe: 'describe',
  Keep: 'keep',
  Switch: 'switch',
} as const;
export type PastDueOfferAction =
  (typeof PAST_DUE_OFFER_ACTIONS)[keyof typeof PAST_DUE_OFFER_ACTIONS];

export const PAST_DUE_SWITCH_TERMS = ['annual', 'quarterly'] as const;
export type PastDueSwitchTerm = (typeof PAST_DUE_SWITCH_TERMS)[number];

export const isPastDueSwitchTerm = (value: unknown): value is PastDueSwitchTerm =>
  typeof value === 'string' && (PAST_DUE_SWITCH_TERMS as readonly string[]).includes(value);

/** The bearer token names the user; nothing in the body is trusted to say who they are. */
export interface PastDueOfferRequest {
  subscriptionId: string;
  action?: PastDueOfferAction;
  term?: PastDueSwitchTerm;
}

/** A renewal cadence as Stripe states it: `interval_count` of `interval`. */
export interface RenewalCadence {
  interval: string;
  intervalCount: number;
}

/**
 * Where the keep offer stands on this subscription:
 *
 * - `available`: a list price whose own discount is below 30%. Accepting credits today's invoice
 *   down to the offer's price (30% off the plan) and fixes the renewal rate there.
 * - `applied`: taken for this invoice (the credit note is on it, or there was nothing to credit)
 *   but the invoice is not paid yet. Accepting again only re-opens the payment page.
 * - `used`: taken on an earlier renewal. The rate it granted is already on the plan.
 * - `kept`: the plan already has 30% or better of its own, or a price that is itself below list
 *   (the $396 annual, the Exclusive Event prices). No credit, no coupon swap: it pays what it owes.
 */
export const PAST_DUE_KEEP_STATUSES = {
  Available: 'available',
  Applied: 'applied',
  Used: 'used',
  Kept: 'kept',
} as const;
export type PastDueKeepStatus =
  (typeof PAST_DUE_KEEP_STATUSES)[keyof typeof PAST_DUE_KEEP_STATUSES];

export interface PastDueKeepArm {
  status: PastDueKeepStatus;
  /**
   * The discount today's invoice comes to, off its undiscounted amount: the offer's 30% once an
   * available keep credits it, or what the invoice already comes to when there is nothing to credit.
   */
  todayPercent: number;
  /**
   * What every renewal after today is discounted by, off list, as a percentage. The offer's 30%
   * unless the plan keeps a rate of its own; `null` when that is a flat amount (legacy "Permanent
   * Dashboard" coupons), which `thenPerPeriodCents` states in money instead.
   */
  renewalPercent: number | null;
  /** True when the plan's own rate stays: 30% or better, or a price below list. */
  keepsExistingDiscount: boolean;
  /** What the invoice asks for once the offer is applied (or already asks, when it is). */
  amountDueTodayCents: number;
  /** Every renewal after today. */
  thenPerPeriodCents: number;
  /**
   * The list price per period when the plan's own price is below it (the $50 Exclusive Event
   * monthly against $75): what new members pay, which the card names beside their rate. Null for a
   * plan whose price is the list price.
   */
  listPerPeriodCents: number | null;
  currency: string;
  cadence: RenewalCadence;
}

export interface PastDueSwitchArm {
  percent: number;
  term: PastDueSwitchTerm;
  priceId: string;
  /** The first discounted charge of the new term. */
  amountTodayCents: number;
  thenPerPeriodCents: number;
  currency: string;
  cadence: RenewalCadence;
}

export interface PastDueOfferDescription {
  subscriptionId: string;
  invoiceId: string;
  /** What the open invoice still asks for, as it stands right now. */
  amountDueCents: number;
  currency: string;
  /** The open invoice's payment page, for paying it as it stands. */
  hostedInvoiceUrl: string | null;
  keep: PastDueKeepArm;
  switch?: PastDueSwitchArm;
}

export interface PastDueKeepResult {
  action: typeof PAST_DUE_OFFER_ACTIONS.Keep;
  hostedInvoiceUrl: string;
  /** What the invoice asks for after the credit note. */
  amountDueCents: number;
}

export interface PastDueSwitchResult {
  action: typeof PAST_DUE_OFFER_ACTIONS.Switch;
  /** The Checkout Session to send the customer to. */
  url: string;
  sessionId: string;
}

export type PastDueOfferResponse =
  | PastDueOfferDescription
  | PastDueKeepResult
  | PastDueSwitchResult;

/**
 * Wire contract for `GET /api/v1/my/past-due`, the light read behind the site-wide past-due
 * banner. One row per subscription whose renewal is overdue on the signed-in account; the open
 * invoice's hosted page is the "Pay now" target.
 */
export interface PastDueNoticeSubscription {
  externalSubscriptionId: string;
  /** `SubscriptionType` value, as the settings API reports it. */
  type: string;
  displayName: string;
  invoiceId: string | null;
  /** What the open invoice still asks for; null when Stripe could not be read. */
  amountDueCents: number | null;
  currency: string;
  hostedInvoiceUrl: string | null;
}

export interface PastDueNoticeResponse {
  subscriptions: PastDueNoticeSubscription[];
}
